import React, { useRef, useEffect } from "react";

const CertificateCanvas = ({ data }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { eventName, eventDate, userName } = data;

    // Set high resolution
    canvas.width = 2000;
    canvas.height = 1414; // A4 Aspect Ratio (roughly)

    const drawCertificate = async () => {
      // Clear Canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Background - Pure White
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // ----------------------------------------------------
      // Ornamental Border
      // ----------------------------------------------------
      const padding = 50;
      const borderGap = 20;

      // Outer Navy Border
      ctx.strokeStyle = "#1A237E";
      ctx.lineWidth = 20;
      // ctx.strokeRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2);
      // Draw rounded rect manually or just rect
      ctx.strokeRect(
        padding,
        padding,
        canvas.width - padding * 2,
        canvas.height - padding * 2,
      );

      // Inner Gold Border
      ctx.strokeStyle = "#D4AF37"; // Gold
      ctx.lineWidth = 5;
      ctx.strokeRect(
        padding + borderGap + 10,
        padding + borderGap + 10,
        canvas.width - (padding + borderGap + 10) * 2,
        canvas.height - (padding + borderGap + 10) * 2,
      );

      // Corner Accents (Simple L-shapes for elegance)
      const cornerSize = 100;
      ctx.lineWidth = 5;
      ctx.strokeStyle = "#D4AF37";

      // Top-Left
      ctx.beginPath();
      ctx.moveTo(padding + 40, padding + 40 + cornerSize);
      ctx.lineTo(padding + 40, padding + 40);
      ctx.lineTo(padding + 40 + cornerSize, padding + 40);
      ctx.stroke();

      // Top-Right
      ctx.beginPath();
      ctx.moveTo(canvas.width - (padding + 40 + cornerSize), padding + 40);
      ctx.lineTo(canvas.width - (padding + 40), padding + 40);
      ctx.lineTo(canvas.width - (padding + 40), padding + 40 + cornerSize);
      ctx.stroke();

      // Bottom-Left
      ctx.beginPath();
      ctx.moveTo(padding + 40, canvas.height - (padding + 40 + cornerSize));
      ctx.lineTo(padding + 40, canvas.height - (padding + 40));
      ctx.lineTo(padding + 40 + cornerSize, canvas.height - (padding + 40));
      ctx.stroke();

      // Bottom-Right
      ctx.beginPath();
      ctx.moveTo(
        canvas.width - (padding + 40 + cornerSize),
        canvas.height - (padding + 40),
      );
      ctx.lineTo(canvas.width - (padding + 40), canvas.height - (padding + 40));
      ctx.lineTo(
        canvas.width - (padding + 40),
        canvas.height - (padding + 40 + cornerSize),
      );
      ctx.stroke();

      // ----------------------------------------------------
      // Helper: Wrap Text
      // ----------------------------------------------------
      const wrapText = (context, text, x, y, maxWidth, lineHeight) => {
        const words = text.split(" ");
        let line = "";
        let currentY = y;

        for (let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + " ";
          const metrics = context.measureText(testLine);
          const testWidth = metrics.width;
          if (testWidth > maxWidth && n > 0) {
            context.fillText(line, x, currentY);
            line = words[n] + " ";
            currentY += lineHeight;
          } else {
            line = testLine;
          }
        }
        context.fillText(line, x, currentY);
        return currentY; // Return last Y position
      };

      // ----------------------------------------------------
      // Header Section
      // ----------------------------------------------------

      // Load Company Logo
      const logoFunc = () =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.src = process.env.REACT_APP_LOGO_URL || "/logo.png";
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
        });

      const logoImg = await logoFunc();

      if (logoImg) {
        // Draw Logo Top Center
        const logoHeight = 180; // Slightly smaller to fit better
        const scale = logoHeight / logoImg.height;
        const logoWidth = logoImg.width * scale;
        ctx.drawImage(
          logoImg,
          (canvas.width - logoWidth) / 2,
          80,
          logoWidth,
          logoHeight,
        );
      }

      // "CERTIFICATE"
      ctx.fillStyle = "#1A237E";
      ctx.font = "bold 90px 'Cinzel', serif"; // Slightly smaller, clean
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic"; // Reset to standard for easier positioning
      ctx.fillText("CERTIFICATE", canvas.width / 2, 360);

      // "OF APPRECIATION"
      ctx.fillStyle = "#D4AF37";
      ctx.font = "bold 40px 'Cinzel', serif";
      ctx.letterSpacing = "8px";
      ctx.fillText("OF APPRECIATION", canvas.width / 2, 420);

      // ----------------------------------------------------
      // Recipient Section
      // ----------------------------------------------------

      // "PROUDLY PRESENTED TO"
      ctx.fillStyle = "#333";
      ctx.font = "500 25px 'Roboto', sans-serif";
      ctx.letterSpacing = "4px";
      ctx.fillText("PROUDLY PRESENTED TO", canvas.width / 2, 550);

      // User Name
      const name = userName || "Participant Name";
      ctx.fillStyle = "#000";
      ctx.font = "120px 'Great Vibes', cursive";
      ctx.fillText(name, canvas.width / 2, 700);

      // Underline
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#D4AF37";
      ctx.beginPath();
      ctx.moveTo(canvas.width / 2 - 300, 720);
      ctx.lineTo(canvas.width / 2 + 300, 720);
      ctx.stroke();

      // ----------------------------------------------------
      // Body Text & Details
      // ----------------------------------------------------

      const bodyCenterY = 820;
      const maxWidth = 1400; // Constrain width for wrapping

      ctx.fillStyle = "#444";
      ctx.font = "35px 'Roboto', sans-serif";

      // Text 1
      const text1 =
        "For their invaluable contribution and active participation in";
      let nextY = wrapText(
        ctx,
        text1,
        canvas.width / 2,
        bodyCenterY,
        maxWidth,
        50,
      );

      // Event Name
      ctx.fillStyle = "#1A237E";
      ctx.font = "bold 60px 'Cinzel', serif";
      nextY += 80;
      ctx.fillText(eventName || "Event Name", canvas.width / 2, nextY);

      // Date Text
      nextY += 70;
      ctx.fillStyle = "#555";
      ctx.font = "italic 30px 'Roboto', sans-serif";
      const dateStr = eventDate
        ? new Date(eventDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "Date";
      ctx.fillText(`Held on ${dateStr}`, canvas.width / 2, nextY);

      // Text 2
      nextY += 80;
      ctx.fillStyle = "#444";
      ctx.font = "30px 'Roboto', sans-serif";
      const text2 =
        "Your dedication towards learning and growth is truly commendable.";
      wrapText(ctx, text2, canvas.width / 2, nextY, maxWidth, 45);

      // ----------------------------------------------------
      // Footer (Signatures)
      // ----------------------------------------------------
      const footerY = 1200;

      // Single Signature - Centered

      // Load Signature
      const sigFunc = () =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.src = process.env.REACT_APP_SIGNATURE_URL || "/signature.jpg";
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
        });

      const sigImg = await sigFunc();

      if (sigImg) {
        const sigHeight = 150;
        const scale = sigHeight / sigImg.height;
        const sigWidth = sigImg.width * scale;
        // Draw signature centered above the line
        ctx.drawImage(
          sigImg,
          (canvas.width - sigWidth) / 2,
          footerY - 140,
          sigWidth,
          sigHeight,
        );
      }

      // Line
      ctx.strokeStyle = "#333";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(canvas.width / 2 - 250, footerY);
      ctx.lineTo(canvas.width / 2 + 250, footerY);
      ctx.stroke();

      // Name & Title
      ctx.font = "bold 35px 'Cinzel', serif";
      ctx.fillStyle = "#1A237E";
      ctx.fillText("MR. ATUL KUMAR", canvas.width / 2, footerY + 50);

      ctx.font = "500 25px 'Roboto', sans-serif";
      ctx.fillText("CEO, ETHICCRAFT", canvas.width / 2, footerY + 90);

      // Tagline
      ctx.fillStyle = "#D4AF37";
      ctx.font = "bold 30px 'Cinzel', serif";
      ctx.fillText(
        "ETHICCRAFT - MARCHING TOWARDS VALUES",
        canvas.width / 2,
        1320,
      );
    };

    // Need to wait for fonts to load for best result
    document.fonts.ready.then(() => {
      drawCertificate();
    });
  }, [data]);

  const downloadCertificate = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = `Certificate_${data.userName || "User"}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div style={{ textAlign: "center", margin: "20px" }}>
      <div
        className="canvas-container"
        style={{
          maxWidth: "100%",
          overflow: "auto",
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          borderRadius: "10px",
          display: "inline-block",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "auto",
            maxWidth: "800px",
            display: "block",
          }}
        />
      </div>
      <br />
      <button
        onClick={downloadCertificate}
        style={{
          marginTop: "20px",
          padding: "12px 30px",
          fontSize: "18px",
          backgroundColor: "#1A237E",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          fontFamily: '"Roboto", sans-serif',
        }}
      >
        Download High-Res Certificate
      </button>
    </div>
  );
};

export default CertificateCanvas;
